import type { IRoadmap } from './types';

const COL = (n: number) => 380 + n * 215;
const ROW = (base: number, r: number) => base + r * 170;

const P0 = 120;
const P1 = 680;
const P2 = 1240;
const P3 = 1800;
const P4 = 2360;
const P5 = 2920;
const P6 = 3480;

export const roadmap: IRoadmap = {
  meta: {
    project: 'LUCKIFY',
    description:
      'Карта развития медицинской платформы LUCKIFY — что уже работает, что в планах. Врачи назначают пациентам анализы, пациенты оплачивают их со скидкой онлайн, а врачам начисляется вознаграждение. Анализы выполняет лаборатория KDL.',
    updatedAt: '2026-06-16',
    sourceFiles: [
      'docs/PRD.md',
      'docs/PROGRESS.md',
      'docs/ADMIN_SPEC.md',
      'docs/SYNC_ARCHITECTURE.md',
      'docs/SECURITY_AUDIT.md',
      'docs/CHANGELOG.md',
      'docs/runbooks/monitoring.md',
      'docs/adr/',
    ],
  },
  phases: [
    {
      id: 'phase-0',
      index: 0,
      title: 'Фундамент',
      subtitle: 'Основа платформы: данные, вход, безопасность',
      weight: 0.15,
      color: '#0f766e',
      accent: 'rgba(15, 118, 110, 0.14)',
      heroPosition: { x: 40, y: P0 + 170 },
      tasks: [
        {
          id: 'p0-prisma-base',
          title: 'Хранение основных данных',
          description:
            'Как платформа хранит сведения о врачах, пациентах, их близких и кодах из SMS',
          status: 'done',
          category: 'database',
          files: ['packages/db/prisma/schema/'],
          position: { x: COL(0), y: ROW(P0, 0) },
        },
        {
          id: 'p0-prisma-extended',
          title: 'Хранение заказов и денег',
          description: 'Заказы, платежи, начисления и выплаты, каталог анализов, кураторы',
          status: 'done',
          category: 'database',
          files: ['packages/db/prisma/schema/'],
          position: { x: COL(1), y: ROW(P0, 0) },
          dependsOn: ['p0-prisma-base'],
        },
        {
          id: 'p0-migrations',
          title: 'Настройка базы данных',
          description: 'База данных развёрнута и поддерживается в актуальном виде',
          status: 'done',
          category: 'database',
          position: { x: COL(2), y: ROW(P0, 0) },
          dependsOn: ['p0-prisma-extended'],
        },
        {
          id: 'p0-prisma7',
          title: 'Обновление движка базы данных',
          description: 'Переход на актуальную версию системы хранения данных — быстрее и надёжнее',
          status: 'done',
          category: 'database',
          position: { x: COL(3), y: ROW(P0, 0) },
          dependsOn: ['p0-prisma-base'],
        },
        {
          id: 'p0-pwa-manifests',
          title: 'Установка как приложение',
          description:
            'Сайт можно установить на телефон как обычное приложение — иконка на экране, запуск без браузера',
          status: 'done',
          category: 'infra',
          files: [
            'apps/partner/public/manifest.webmanifest',
            'apps/client/public/manifest.webmanifest',
          ],
          position: { x: COL(4), y: ROW(P0, 0) },
        },
        {
          id: 'p0-auth',
          title: 'Вход по номеру телефона',
          description:
            'Регистрация и вход по коду из SMS; сессия безопасно продлевается, при подозрительной активности доступ блокируется',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/auth/'],
          endpoints: [
            'POST /api/auth/send-code',
            'POST /api/auth/verify-code',
            'POST /api/auth/refresh',
            'POST /api/auth/logout',
          ],
          position: { x: COL(0), y: ROW(P0, 1) },
          dependsOn: ['p0-prisma-base'],
        },
        {
          id: 'p0-sms-stub',
          title: 'Коды входа в тестовом режиме',
          description:
            'Во время разработки код входа фиксированный; личные данные в технических записях скрыты',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/integrations/sms/', 'packages/utils/src/mask.ts'],
          position: { x: COL(1), y: ROW(P0, 1) },
          dependsOn: ['p0-auth'],
        },
        {
          id: 'p0-guards',
          title: 'Разграничение доступа',
          description:
            'Каждый видит только своё: врач — свои заказы, пациент — свои, сотрудник — по своей роли',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/auth/jwt-auth.guard.ts', 'apps/server/src/admin/guards/'],
          position: { x: COL(2), y: ROW(P0, 1) },
          dependsOn: ['p0-auth'],
        },
        {
          id: 'p0-rate-limit',
          title: 'Защита от перебора и перегрузки',
          description:
            'Ограничение частоты запросов и защитные настройки — платформу нельзя сломать перебором или нагрузкой',
          status: 'done',
          category: 'infra',
          files: ['apps/server/src/main.ts', 'apps/server/src/app/app.module.ts'],
          position: { x: COL(3), y: ROW(P0, 1) },
        },
        {
          id: 'p0-encryption',
          title: 'Шифрование персональных данных',
          description:
            'ФИО, телефоны и почта хранятся в зашифрованном виде — даже при утечке базы прочитать их нельзя (152-ФЗ)',
          status: 'done',
          category: 'database',
          files: [
            'packages/db/src/encryption.ts',
            'docs/adr/0001-pii-encryption.md',
            'docs/runbooks/CRYPTO_KEYS.md',
          ],
          position: { x: COL(4), y: ROW(P0, 1) },
        },
        {
          id: 'p0-horizontal-scaling',
          title: 'Готовность к росту нагрузки',
          description:
            'Платформу можно запустить в несколько копий при росте числа пользователей — без сбоев и задвоений',
          status: 'done',
          category: 'infra',
          files: ['docs/adr/0028-horizontal-scaling-singleton-workers.md'],
          position: { x: COL(0), y: ROW(P0, 2) },
        },
        {
          id: 'p0-stale-chunks',
          title: 'Бесшовные обновления',
          description:
            'После выпуска новой версии у открытых вкладок не возникает ошибок — обновление подхватывается само',
          status: 'done',
          category: 'infra',
          files: [
            'packages/ui/src/utils/staleChunk/',
            'docs/adr/0037-frontend-update-delivery-stale-chunks.md',
          ],
          position: { x: COL(1), y: ROW(P0, 2) },
        },
        {
          id: 'p0-db-selfheal',
          title: 'Самовосстановление базы',
          description:
            'Платформа сама проверяет и восстанавливает права доступа к базе данных при запуске',
          status: 'done',
          category: 'infra',
          files: ['docs/adr/0017-db-privilege-self-heal-and-preflight.md'],
          position: { x: COL(2), y: ROW(P0, 2) },
        },
      ],
    },
    {
      id: 'phase-1',
      index: 1,
      title: 'Приложение для врачей',
      subtitle: 'Личный кабинет врача',
      weight: 0.15,
      color: '#6c5ce7',
      accent: 'rgba(108, 92, 231, 0.15)',
      heroPosition: { x: 40, y: P1 + 170 },
      tasks: [
        {
          id: 'p1-auth-flow',
          title: 'Вход и регистрация врача',
          description: 'Телефон → код из SMS → ФИО → проверка сотрудником → доступ открыт',
          status: 'done',
          category: 'frontend-partner',
          pages: ['phone', 'otp', 'personal', 'verification'],
          position: { x: COL(0), y: ROW(P1, 0) },
        },
        {
          id: 'p1-clients',
          title: 'Справочник пациентов',
          description:
            'Врач ведёт список своих пациентов: имя и телефон, быстрый выбор при оформлении заказа',
          status: 'done',
          category: 'frontend-partner',
          pages: ['selectClient', 'newClient'],
          endpoints: ['GET /api/partner/clients', 'POST /api/partner/clients'],
          position: { x: COL(1), y: ROW(P1, 0) },
        },
        {
          id: 'p1-catalog',
          title: 'Каталог анализов',
          description: 'Каталог с разделами и поиском, карточка анализа, избранное',
          status: 'done',
          category: 'frontend-partner',
          pages: ['catalog', 'analysisDetail'],
          endpoints: ['GET /api/catalog/analyses', 'GET /api/catalog/categories'],
          position: { x: COL(2), y: ROW(P1, 0) },
        },
        {
          id: 'p1-profile',
          title: 'Профиль врача',
          description: 'ФИО, специализация, контакты и юридические документы',
          status: 'done',
          category: 'frontend-partner',
          pages: ['profile'],
          endpoints: ['GET /api/partner/profile', 'PUT /api/partner/profile'],
          position: { x: COL(3), y: ROW(P1, 0) },
        },
        {
          id: 'p1-accept-offer',
          title: 'Принятие условий сотрудничества',
          description:
            'При регистрации врач принимает договор-оферту и правила платформы; фиксируется версия документа',
          status: 'done',
          category: 'backend',
          position: { x: COL(4), y: ROW(P1, 0) },
          dependsOn: ['p1-profile'],
        },
        {
          id: 'p1-create-order',
          title: 'Оформление заказа',
          description:
            'Пациент → город → анализы → итог → отправка. Сразу виден расчёт вознаграждения врача',
          status: 'done',
          category: 'frontend-partner',
          pages: ['selectCity', 'summary', 'success'],
          endpoints: [
            'POST /api/partner/orders',
            'POST /api/partner/orders/preview',
            'GET /api/partner/orders',
          ],
          position: { x: COL(0), y: ROW(P1, 1) },
          dependsOn: ['p1-clients', 'p1-catalog'],
        },
        {
          id: 'p1-orders-list',
          title: 'История заказов',
          description: 'Список заказов с фильтрами по статусу, детали и правка состава заказа',
          status: 'done',
          category: 'frontend-partner',
          pages: ['orders/list', 'orders/detail'],
          endpoints: ['PUT /api/partner/orders/:id', 'DELETE /api/partner/orders/:id'],
          position: { x: COL(1), y: ROW(P1, 1) },
        },
        {
          id: 'p1-qr',
          title: 'QR-код и ссылка на оплату',
          description: 'Врач показывает пациенту QR-код или делится ссылкой на оплату заказа',
          status: 'done',
          category: 'frontend-partner',
          files: ['apps/partner/src/shared/ui/OrderQrModal/'],
          position: { x: COL(2), y: ROW(P1, 1) },
          dependsOn: ['p1-create-order'],
        },
        {
          id: 'p1-send-sms',
          title: 'SMS пациенту со ссылкой',
          description:
            'При оформлении заказа пациенту автоматически приходит SMS со ссылкой на оплату',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/order/order.service.ts'],
          position: { x: COL(3), y: ROW(P1, 1) },
          dependsOn: ['p1-create-order'],
        },
        {
          id: 'p1-favorites',
          title: 'Избранные анализы',
          description:
            'Часто назначаемые анализы врач отмечает звёздочкой — они собираются в отдельный раздел',
          status: 'done',
          category: 'frontend-partner',
          files: [
            'apps/server/src/partner/favorites/',
            'docs/adr/0042-partner-favorite-analyses.md',
          ],
          endpoints: [
            'GET /api/partner/favorites',
            'POST /api/partner/favorites/:analysisId',
            'DELETE /api/partner/favorites/:analysisId',
          ],
          position: { x: COL(4), y: ROW(P1, 1) },
          dependsOn: ['p1-catalog'],
        },
        {
          id: 'p1-earnings',
          title: 'Заработок и баланс',
          description: 'Текущий баланс и история начислений; сумма на экране плавно обновляется',
          status: 'done',
          category: 'frontend-partner',
          pages: ['main', 'finance'],
          endpoints: ['GET /api/partner/earnings', 'GET /api/partner/earnings/history'],
          position: { x: COL(0), y: ROW(P1, 2) },
        },
        {
          id: 'p1-payout-request',
          title: 'Заявка на вывод денег',
          description: 'Раздел «Финансы»: баланс, активная заявка, запрос вывода с комментарием',
          status: 'done',
          category: 'frontend-partner',
          pages: ['finance'],
          endpoints: ['POST /api/partner/earnings/payouts/request'],
          files: ['apps/partner/src/pages/finance/', 'docs/adr/0011-manual-payout-flow.md'],
          position: { x: COL(1), y: ROW(P1, 2) },
          dependsOn: ['p1-earnings'],
        },
        {
          id: 'p1-partner-payout-requisites',
          title: 'Реквизиты для выплат',
          description:
            'Куда перечислять деньги: СБП или карта, банк, ФИО, ИНН. Данные хранятся в зашифрованном виде',
          status: 'done',
          category: 'frontend-partner',
          pages: ['finance/requisites'],
          endpoints: [
            'GET /api/partner/profile/payout-requisites',
            'PUT /api/partner/profile/payout-requisites',
          ],
          position: { x: COL(2), y: ROW(P1, 2) },
          dependsOn: ['p1-payout-request'],
        },
        {
          id: 'p1-partner-payouts-history',
          title: 'История выплат',
          description: 'Вкладки «Начисления» и «Выплаты» со статусами заявок и причинами',
          status: 'done',
          category: 'frontend-partner',
          endpoints: ['GET /api/partner/earnings/payouts'],
          position: { x: COL(3), y: ROW(P1, 2) },
          dependsOn: ['p1-payout-request'],
        },
      ],
    },
    {
      id: 'phase-2',
      index: 2,
      title: 'Приложение для пациентов',
      subtitle: 'Личный кабинет пациента',
      weight: 0.15,
      color: '#f97316',
      accent: 'rgba(249, 115, 22, 0.13)',
      heroPosition: { x: 40, y: P2 + 170 },
      tasks: [
        {
          id: 'p2-auth',
          title: 'Вход пациента',
          description: 'Открытая регистрация и вход по коду из SMS',
          status: 'done',
          category: 'frontend-client',
          pages: ['phone', 'otp', 'profileSetup'],
          position: { x: COL(0), y: ROW(P2, 0) },
        },
        {
          id: 'p2-orders',
          title: 'Список заказов',
          description: 'Главный экран: неоплаченные заказы от всех врачей пациента',
          status: 'done',
          category: 'frontend-client',
          pages: ['orders/list', 'orders/detail'],
          endpoints: ['GET /api/client/orders'],
          position: { x: COL(1), y: ROW(P2, 0) },
        },
        {
          id: 'p2-self-order',
          title: 'Самостоятельный заказ',
          description:
            'Пациент сам выбирает анализы, город и отделение; на каждом шаге понятно, что делать дальше',
          status: 'done',
          category: 'frontend-client',
          pages: [
            'order/catalog',
            'order/selectCity',
            'order/selectBranch',
            'order/summary',
            'order/success',
          ],
          endpoints: ['POST /api/client/orders/self', 'POST /api/client/orders/self/preview'],
          position: { x: COL(2), y: ROW(P2, 0) },
        },
        {
          id: 'p2-dependents',
          title: 'Заказ для близких',
          description:
            'Пациент добавляет членов семьи и оформляет анализы на них; для детей — согласие законного представителя',
          status: 'done',
          category: 'backend',
          endpoints: ['GET/POST/PUT/DELETE /api/client/dependents'],
          position: { x: COL(3), y: ROW(P2, 0) },
        },
        {
          id: 'p2-profile',
          title: 'Профиль (медкарта)',
          description:
            'ФИО, пол, дата рождения, почта, город. Выгрузка своих данных и удаление аккаунта',
          status: 'done',
          category: 'frontend-client',
          pages: ['profile'],
          position: { x: COL(4), y: ROW(P2, 0) },
        },
        {
          id: 'p2-labs',
          title: 'Где сдать анализы',
          description:
            'Точки лабораторий на карте, фильтр по городу и клинике, маршрут в навигаторе',
          status: 'done',
          category: 'frontend-client',
          pages: ['labs'],
          endpoints: ['GET /api/branches'],
          position: { x: COL(0), y: ROW(P2, 1) },
        },
        {
          id: 'p2-results',
          title: 'Мои анализы',
          description: 'Список результатов и их статусы (получение из лаборатории — см. план ниже)',
          status: 'done',
          category: 'frontend-client',
          pages: ['results/list', 'results/detail'],
          endpoints: ['GET /api/client/results', 'GET /api/client/results/:id'],
          position: { x: COL(1), y: ROW(P2, 1) },
        },
        {
          id: 'p2-pay',
          title: 'Оплата онлайн',
          description:
            'Оплата через платёжный сервис: проверка данных → оплата → отслеживание статуса',
          status: 'done',
          category: 'backend',
          endpoints: ['POST /api/client/orders/:id/pay'],
          position: { x: COL(2), y: ROW(P2, 1) },
          dependsOn: ['p2-orders'],
        },
        {
          id: 'p2-webhook',
          title: 'Подтверждение оплаты',
          description:
            'Платёжный сервис сам сообщает платформе, что заказ оплачен; повторные сообщения не создают дублей',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/integrations/cloudpayments/'],
          position: { x: COL(3), y: ROW(P2, 1) },
          dependsOn: ['p2-pay'],
        },
        {
          id: 'p2-accrual-on-pay',
          title: 'Начисление врачу после оплаты',
          description:
            'После оплаты врачу автоматически начисляется вознаграждение; при возврате денег начисление отменяется',
          status: 'done',
          category: 'backend',
          position: { x: COL(4), y: ROW(P2, 1) },
          dependsOn: ['p2-pay'],
        },
        {
          id: 'p2-pdf',
          title: 'PDF с результатами',
          description:
            'Скачивание и отправка результатов в PDF — появится после подключения выдачи результатов из лаборатории',
          status: 'todo',
          category: 'backend',
          position: { x: COL(0), y: ROW(P2, 2) },
          dependsOn: ['p2-results'],
        },
        {
          id: 'p2-delete-account',
          title: 'Удаление аккаунта',
          description:
            'Подтверждение по SMS и полное обезличивание личных данных по требованию пользователя',
          status: 'done',
          category: 'backend',
          endpoints: [
            'POST /api/client/account/deletion/request-code',
            'DELETE /api/client/account',
          ],
          position: { x: COL(1), y: ROW(P2, 2) },
        },
        {
          id: 'client-select-branch',
          title: 'Выбор отделения пациентом',
          description: 'Пациент сам выбирает удобное отделение лаборатории при оформлении заказа',
          status: 'done',
          category: 'frontend-client',
          pages: ['order/selectBranch'],
          endpoints: ['POST /api/client/orders/:id/branch'],
          files: ['docs/adr/0031-patient-chooses-lab-branch.md'],
          position: { x: COL(2), y: ROW(P2, 2) },
        },
        {
          id: 'p2-pii-confirm',
          title: 'Подтверждение данных пациента',
          description:
            'Пациент подтверждает ФИО, дату рождения и пол — именно эти данные уходят в лабораторию',
          status: 'done',
          category: 'frontend-client',
          endpoints: ['POST /api/client/orders/:id/confirm-patient'],
          files: ['docs/adr/0029-patient-pii-source-of-truth.md'],
          position: { x: COL(3), y: ROW(P2, 2) },
        },
        {
          id: 'p2-magic-link',
          title: 'Оплата по ссылке',
          description:
            'Пациент открывает заказ по короткой ссылке и оплачивает — без отдельной регистрации',
          status: 'done',
          category: 'frontend-client',
          pages: ['order/magic'],
          endpoints: ['GET /api/client/orders/by-code/:shortCode'],
          position: { x: COL(4), y: ROW(P2, 2) },
        },
      ],
    },
    {
      id: 'phase-3',
      index: 3,
      title: 'Панель управления',
      subtitle: 'Рабочее место сотрудников платформы',
      weight: 0.15,
      color: '#0ea5e9',
      accent: 'rgba(14, 165, 233, 0.14)',
      heroPosition: { x: 40, y: P3 + 170 },
      tasks: [
        {
          id: 'p3-admin-auth',
          title: 'Вход сотрудников',
          description: 'Создание первого администратора, вход по почте и паролю, безопасная сессия',
          status: 'done',
          category: 'backend',
          endpoints: [
            'POST /api/admin/bootstrap',
            'POST /api/admin/auth/login',
            'POST /api/admin/auth/refresh',
            'POST /api/admin/auth/logout',
          ],
          position: { x: COL(0), y: ROW(P3, 0) },
        },
        {
          id: 'p3-administrators',
          title: 'Управление сотрудниками',
          description: 'Добавление сотрудников и их роли (доступно только главному администратору)',
          status: 'done',
          category: 'frontend-admin',
          pages: ['administrators'],
          endpoints: ['GET/POST/PUT/DELETE /api/admin/administrators'],
          position: { x: COL(1), y: ROW(P3, 0) },
          dependsOn: ['p3-admin-auth'],
        },
        {
          id: 'p3-partners-admin',
          title: 'Проверка врачей',
          description: 'Заявки врачей: одобрить или отклонить, заблокировать, назначить куратора',
          status: 'done',
          category: 'frontend-admin',
          pages: ['partners', 'partners/[id]'],
          endpoints: [
            'GET /api/admin/partners',
            'PUT /api/admin/partners/:id/verify',
            'PUT /api/admin/partners/:id/curator',
          ],
          position: { x: COL(2), y: ROW(P3, 0) },
        },
        {
          id: 'p3-users-admin',
          title: 'Список пользователей',
          description:
            'Поиск, постраничный просмотр, статус врача, удаление личных данных по запросу',
          status: 'done',
          category: 'frontend-admin',
          pages: ['users'],
          endpoints: ['GET /api/admin/users', 'DELETE /api/admin/users/:id/pii'],
          position: { x: COL(3), y: ROW(P3, 0) },
        },
        {
          id: 'p3-orders-admin',
          title: 'Заказы (для сотрудников)',
          description:
            'Фильтры по статусу, дате и лаборатории; ручная отправка заказа и отмена записи',
          status: 'done',
          category: 'frontend-admin',
          pages: ['orders'],
          endpoints: [
            'GET /api/admin/orders',
            'POST /api/admin/orders/:id/submit',
            'POST /api/admin/orders/:id/cancel-booking',
            'POST /api/admin/orders/:id/sync-status',
          ],
          position: { x: COL(4), y: ROW(P3, 0) },
        },
        {
          id: 'p3-stats',
          title: 'Сводка',
          description: 'Карточки: новые заявки врачей, заказы за день, выручка',
          status: 'done',
          category: 'frontend-admin',
          pages: ['dashboard'],
          endpoints: ['GET /api/admin/stats'],
          position: { x: COL(5), y: ROW(P3, 0) },
        },
        {
          id: 'p3-analytics',
          title: 'Аналитика',
          description:
            'Ежедневные показатели, воронка продаж, разрезы по городам и врачам; сегодняшний день виден сразу',
          status: 'done',
          category: 'frontend-admin',
          pages: ['analytics'],
          endpoints: [
            'GET /api/admin/analytics/overview',
            'GET /api/admin/analytics/timeseries',
            'GET /api/admin/analytics/breakdown/cities',
            'GET /api/admin/analytics/breakdown/partners',
            'POST /api/admin/analytics/snapshot/backfill',
          ],
          position: { x: COL(0), y: ROW(P3, 1) },
        },
        {
          id: 'p3-payouts',
          title: 'Выплаты врачам',
          description: 'Список заявок и их движение: создана → в обработке → выплачена / ошибка',
          status: 'done',
          category: 'frontend-admin',
          pages: ['payouts'],
          endpoints: ['GET /api/admin/payouts', 'PUT /api/admin/payouts/:id'],
          position: { x: COL(1), y: ROW(P3, 1) },
        },
        {
          id: 'p3-admin-payout-detail',
          title: 'Карточка выплаты',
          description:
            'Реквизиты, начисления и ответственный сотрудник; кнопки копирования и подтверждения, пометка тестовых заявок',
          status: 'done',
          category: 'frontend-admin',
          pages: ['payouts/[id]'],
          endpoints: ['GET /api/admin/payouts/:id'],
          position: { x: COL(2), y: ROW(P3, 1) },
          dependsOn: ['p3-payouts'],
        },
        {
          id: 'admin-payments',
          title: 'Платежи',
          description:
            'Список платежей с фильтрами и разбор спорных ситуаций (например, оплата и возврат)',
          status: 'done',
          category: 'frontend-admin',
          pages: ['payments'],
          endpoints: ['GET /api/admin/payments'],
          position: { x: COL(3), y: ROW(P3, 1) },
        },
        {
          id: 'p3-clinics',
          title: 'Настройки клиник',
          description:
            'Размер скидки и вознаграждения, срок действия цены, пауза автоматической отправки в лабораторию',
          status: 'done',
          category: 'frontend-admin',
          pages: ['settings'],
          endpoints: ['GET /api/admin/clinics', 'PUT /api/admin/clinics/:id/settings'],
          position: { x: COL(4), y: ROW(P3, 1) },
        },
        {
          id: 'p3-catalog-admin',
          title: 'Управление каталогом',
          description: 'Добавление и правка анализов и разделов, биоматериал, цены по городам',
          status: 'done',
          category: 'frontend-admin',
          pages: ['catalog'],
          endpoints: [
            'GET/PATCH /api/admin/catalog/analyses',
            'PATCH /api/admin/catalog/analyses/:id/prices/:cityId',
            'CRUD /api/admin/catalog/categories',
          ],
          position: { x: COL(5), y: ROW(P3, 1) },
        },
        {
          id: 'p3-categorization',
          title: 'Распределение анализов по разделам',
          description:
            'Подсказки, к какому разделу отнести анализ, с массовым применением и предпросмотром',
          status: 'done',
          category: 'frontend-admin',
          pages: ['categorization'],
          endpoints: [
            'GET /api/admin/categorization/uncategorized',
            'POST /api/admin/categorization/bulk-assign',
            'POST /api/admin/categorization/auto-assign',
          ],
          files: ['docs/adr/0005-hybrid-classifier-keyword-tfidf.md'],
          position: { x: COL(0), y: ROW(P3, 2) },
        },
        {
          id: 'p3-biomaterials',
          title: 'Биоматериалы и услуги забора',
          description:
            'Справочник видов биоматериала и услуг забора (взятие крови и т.п.) с ценами по городам',
          status: 'done',
          category: 'frontend-admin',
          pages: ['biomaterials'],
          endpoints: [
            'CRUD /api/admin/biomaterials/types',
            'CRUD /api/admin/biomaterials/collection-services',
          ],
          position: { x: COL(1), y: ROW(P3, 2) },
        },
        {
          id: 'p3-branches-admin',
          title: 'Управление отделениями',
          description:
            'Ручное добавление и правка отделений лабораторий (пока отделения только из автообновления)',
          status: 'todo',
          category: 'backend',
          endpoints: ['CRUD /api/admin/branches'],
          position: { x: COL(2), y: ROW(P3, 2) },
        },
        {
          id: 'p3-sync-admin',
          title: 'Обновление каталога и проверка связи',
          description:
            'Запуск обновления цен, проверка связи с лабораторией, тестовый заказ; идёт в фоне, прогресс сохраняется',
          status: 'done',
          category: 'frontend-admin',
          pages: ['sync'],
          endpoints: [
            'POST /api/admin/sync',
            'GET /api/admin/sync/status',
            'POST /api/admin/branch-sync',
          ],
          position: { x: COL(3), y: ROW(P3, 2) },
        },
        {
          id: 'p3-monitoring-page',
          title: 'Мониторинг',
          description: 'Балансы сервисов, маржа заказов, расходы и уведомления — в одном разделе',
          status: 'done',
          category: 'frontend-admin',
          pages: ['monitoring'],
          endpoints: [
            'GET /api/admin/monitoring/balances',
            'GET /api/admin/monitoring/spend/summary',
          ],
          position: { x: COL(4), y: ROW(P3, 2) },
        },
        {
          id: 'p3-proxies-page',
          title: 'Подключения для сбора цен',
          description:
            'Управление каналами, через которые платформа собирает цены с сайта лаборатории: список, остаток, продление',
          status: 'done',
          category: 'frontend-admin',
          pages: ['proxies'],
          endpoints: [
            'GET /api/admin/proxies',
            'POST /api/admin/proxies/provision',
            'GET /api/admin/proxies/provision/status',
          ],
          position: { x: COL(5), y: ROW(P3, 2) },
        },
      ],
    },
    {
      id: 'phase-4',
      index: 4,
      title: 'Внешние сервисы',
      subtitle: 'Лаборатория, оплата, SMS, уведомления',
      weight: 0.15,
      color: '#dc2626',
      accent: 'rgba(220, 38, 38, 0.13)',
      heroPosition: { x: 40, y: P4 + 170 },
      tasks: [
        {
          id: 'p4-multi-clinic',
          title: 'Подключение лабораторий',
          description: 'Единый механизм: новые лаборатории добавляются без переделки системы',
          status: 'done',
          category: 'integration',
          files: ['apps/server/src/integrations/', 'docs/adr/0004-multi-clinic-adapter-pattern.md'],
          position: { x: COL(0), y: ROW(P4, 0) },
        },
        {
          id: 'p4-kdl-catalog',
          title: 'Каталог и цены KDL',
          description:
            'Автоматический сбор каталога и цен с сайта лаборатории по городам, отметка популярных анализов',
          status: 'done',
          category: 'integration',
          files: ['apps/server/src/integrations/kdl/'],
          position: { x: COL(1), y: ROW(P4, 0) },
          dependsOn: ['p4-multi-clinic'],
        },
        {
          id: 'p4-kdl-sync',
          title: 'Регулярное обновление каталога',
          description: 'Каталог и цены обновляются по расписанию автоматически, ничего не теряя',
          status: 'done',
          category: 'integration',
          files: ['apps/server/src/integrations/sync/'],
          position: { x: COL(2), y: ROW(P4, 0) },
          dependsOn: ['p4-kdl-catalog'],
        },
        {
          id: 'p4-kdl-proxy-pool',
          title: 'Обход блокировок при сборе цен',
          description:
            'Сайт лаборатории блокирует серверные запросы — используется пул подключений, нерабочие восстанавливаются сами',
          status: 'done',
          category: 'integration',
          files: [
            'apps/server/src/integrations/proxy/',
            'docs/adr/0020-proxy-pool-segmentation-by-purpose.md',
          ],
          position: { x: COL(3), y: ROW(P4, 0) },
          dependsOn: ['p4-kdl-catalog'],
        },
        {
          id: 'p4-kdl-results',
          title: 'Результаты из лаборатории',
          description:
            'Получение готовых результатов (PDF и данные) и их статусов готовности из KDL',
          status: 'todo',
          category: 'integration',
          position: { x: COL(4), y: ROW(P4, 0) },
          dependsOn: ['p4-multi-clinic'],
        },
        {
          id: 'p4-laport-booking',
          title: 'Передача заказа в лабораторию',
          description:
            'Заказ автоматически уходит в систему лаборатории; запись можно поставить на паузу или отменить',
          status: 'done',
          category: 'integration',
          files: ['apps/server/src/integrations/laport/'],
          endpoints: [
            'POST /api/admin/integrations/laport/test-booking',
            'POST /api/admin/integrations/laport/health',
          ],
          position: { x: COL(0), y: ROW(P4, 1) },
          dependsOn: ['p4-multi-clinic'],
        },
        {
          id: 'p4-laport-cities',
          title: 'Справочник городов лаборатории',
          description:
            'Города берутся из системы лаборатории; город определяет, куда направить заказ',
          status: 'done',
          category: 'integration',
          endpoints: ['POST /api/admin/integrations/laport/sync-cities'],
          position: { x: COL(1), y: ROW(P4, 1) },
          dependsOn: ['p4-laport-booking'],
        },
        {
          id: 'p4-laport-status-sync',
          title: 'Статусы заказов из лаборатории',
          description:
            'Платформа узнаёт, сдан ли биоматериал, и только после этого открывает выплату врачу',
          status: 'done',
          category: 'integration',
          files: [
            'apps/server/src/integrations/laport/laport-status.map.ts',
            'docs/adr/0012-laport-status-sync-payout-gate.md',
          ],
          position: { x: COL(2), y: ROW(P4, 1) },
          dependsOn: ['p4-laport-booking'],
        },
        {
          id: 'p4-payment',
          title: 'Приём оплат (CloudPayments)',
          description:
            'Онлайн-оплата с проверкой подлинности и возвратами. Запуск ждёт одобрения платёжного сервиса',
          status: 'done',
          category: 'integration',
          files: [
            'apps/server/src/integrations/cloudpayments/',
            'docs/adr/0013-cloudpayments-dual-terminal-routing.md',
          ],
          position: { x: COL(3), y: ROW(P4, 1) },
          dependsOn: ['p4-multi-clinic'],
        },
        {
          id: 'p4-sms-real',
          title: 'Отправка SMS (sms.ru)',
          description: 'Реальная отправка кодов и сообщений через SMS-сервис с учётом расходов',
          status: 'done',
          category: 'integration',
          files: ['apps/server/src/integrations/sms/'],
          position: { x: COL(4), y: ROW(P4, 1) },
        },
        {
          id: 'p4-proxy-autoprovision',
          title: 'Автопокупка подключений',
          description:
            'Автоматический выпуск и продление каналов для сбора цен; автопокупка пока выключена до ручной проверки',
          status: 'done',
          category: 'integration',
          files: [
            'apps/server/src/integrations/proxy/',
            'docs/adr/0016-proxyline-auto-provisioning.md',
          ],
          position: { x: COL(0), y: ROW(P4, 2) },
          dependsOn: ['p4-kdl-proxy-pool'],
        },
        {
          id: 'p4-telegram',
          title: 'Телеграм-бот',
          description: 'Уведомления команды в Telegram с распределением по темам и группам',
          status: 'done',
          category: 'integration',
          files: ['apps/server/src/telegram/', 'apps/server/src/notifications/'],
          position: { x: COL(1), y: ROW(P4, 2) },
        },
        {
          id: 'p2-branch-sync',
          title: 'Обновление отделений лабораторий',
          description: 'Загрузка списка отделений из системы лаборатории (дорабатывается)',
          status: 'in_progress',
          category: 'integration',
          endpoints: ['POST /api/admin/branch-sync', 'POST /api/admin/branch-sync/:clinicSlug'],
          position: { x: COL(2), y: ROW(P4, 2) },
        },
        {
          id: 'p4-oauth',
          title: 'Вход через соцсети',
          description: 'Регистрация врача через социальные сети',
          status: 'todo',
          category: 'integration',
          position: { x: COL(3), y: ROW(P4, 2) },
        },
      ],
    },
    {
      id: 'phase-5',
      index: 5,
      title: 'Безопасность и контроль',
      subtitle: 'Защита данных (152-ФЗ), безопасность, мониторинг',
      weight: 0.15,
      color: '#16a34a',
      accent: 'rgba(22, 163, 74, 0.13)',
      heroPosition: { x: 40, y: P5 + 170 },
      tasks: [
        {
          id: 'p5-consent-module',
          title: 'Согласия на обработку данных',
          description: 'Учёт согласий с фиксацией версии документа, времени и устройства (152-ФЗ)',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/compliance/', 'docs/adr/0007-152fz-pre-launch-compliance.md'],
          endpoints: [
            'GET /api/consents/documents',
            'GET/POST /api/me/consents',
            'POST /api/me/consents/revoke',
          ],
          position: { x: COL(0), y: ROW(P5, 0) },
        },
        {
          id: 'p5-consent-ui',
          title: 'Окно согласий и юр-страницы',
          description:
            'Баннер о cookie, юридические документы и галочки согласия при регистрации и заказе',
          status: 'done',
          category: 'frontend-client',
          files: [
            'packages/ui/src/components/CookieBanner/',
            'packages/ui/src/components/LegalDocument/',
          ],
          endpoints: ['POST /api/consents/cookies', 'GET /api/consents/documents/:type'],
          position: { x: COL(1), y: ROW(P5, 0) },
          dependsOn: ['p5-consent-module'],
        },
        {
          id: 'p5-consent-admin',
          title: 'Управление версиями документов',
          description:
            'Черновик → предпросмотр → публикация; статистика согласий. Опубликованное не правят — только новая версия',
          status: 'done',
          category: 'frontend-admin',
          pages: ['consents'],
          endpoints: [
            'CRUD /api/admin/consents/documents',
            'POST /api/admin/consents/documents/:id/publish',
          ],
          files: ['docs/adr/0033-legal-documents-lifecycle.md'],
          position: { x: COL(2), y: ROW(P5, 0) },
          dependsOn: ['p5-consent-module'],
        },
        {
          id: 'p5-audit-log',
          title: 'Журнал действий',
          description:
            'Неизменяемая запись важных действий: входы, доступ к данным, операции сотрудников, передача данных в лабораторию',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/compliance/audit.service.ts'],
          position: { x: COL(3), y: ROW(P5, 0) },
        },
        {
          id: 'p5-dsar',
          title: 'Выгрузка своих данных',
          description: 'Пользователь может скачать все свои данные одним файлом (152-ФЗ)',
          status: 'done',
          category: 'backend',
          endpoints: ['GET /api/client/me/export'],
          position: { x: COL(4), y: ROW(P5, 0) },
        },
        {
          id: 'p5-phone-e164',
          title: 'Единый формат телефонов',
          description: 'Все телефоны приводятся к одному формату — единообразно, без ошибок ввода',
          status: 'done',
          category: 'backend',
          files: ['packages/utils/src/phone.ts', 'docs/adr/0006-phone-canonical-format-e164.md'],
          position: { x: COL(0), y: ROW(P5, 1) },
        },
        {
          id: 'p5-scrub-secrets',
          title: 'Скрытие служебных данных',
          description: 'Из ответов и технических записей убираются служебные и чувствительные поля',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/common/interceptors/scrub.interceptor.ts'],
          position: { x: COL(1), y: ROW(P5, 1) },
        },
        {
          id: 'p5-pii-anonymize',
          title: 'Обезличивание при удалении',
          description:
            'При удалении аккаунта личные данные заменяются на обезличенные метки, включая копии в заказах',
          status: 'done',
          category: 'backend',
          position: { x: COL(2), y: ROW(P5, 1) },
        },
        {
          id: 'p5-legal-review',
          title: 'Юридическая вычитка документов',
          description:
            'Реальные тексты юристов уже загружены; остаётся финальная вычитка и реквизиты оператора (ИНН/ОГРН)',
          status: 'in_progress',
          category: 'integration',
          files: ['docs/legal/'],
          position: { x: COL(3), y: ROW(P5, 1) },
        },
        {
          id: 'p5-security-audit',
          title: 'Аудит безопасности',
          description:
            'Сквозная проверка на уязвимости и соответствие 152-ФЗ: критичных проблем нет, 11 замечаний устранены или взяты на контроль',
          status: 'done',
          category: 'backend',
          files: ['docs/SECURITY_AUDIT.md'],
          position: { x: COL(4), y: ROW(P5, 1) },
        },
        {
          id: 'p5-balance-monitoring',
          title: 'Контроль балансов сервисов',
          description:
            'Остатки на SMS, хостинге и подключениях проверяются 3 раза в день с оповещением, когда денег мало',
          status: 'done',
          category: 'backend',
          files: [
            'apps/server/src/monitoring/',
            'docs/adr/0018-balance-monitoring-and-notifications.md',
          ],
          endpoints: [
            'GET /api/admin/monitoring/balances',
            'POST /api/admin/monitoring/balances/check',
          ],
          position: { x: COL(0), y: ROW(P5, 2) },
        },
        {
          id: 'p5-notifications',
          title: 'Уведомления в Telegram',
          description:
            'Оповещения по событиям с распределением по категориям — в рабочий чат команды',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/notifications/'],
          endpoints: ['GET /api/admin/notifications/channels/telegram/status'],
          position: { x: COL(1), y: ROW(P5, 2) },
        },
        {
          id: 'p5-prod-monitoring',
          title: 'Мониторинг работы платформы',
          description:
            'Графики нагрузки, сбор ошибок и логов, мгновенные оповещения в Telegram, если что-то сломалось (Grafana)',
          status: 'done',
          category: 'infra',
          files: [
            'apps/server/src/webhooks/',
            'docs/adr/0040-prod-monitoring-udk-stack-alert-relay.md',
            'docs/runbooks/monitoring.md',
          ],
          endpoints: ['POST /api/webhooks/alertmanager'],
          position: { x: COL(2), y: ROW(P5, 2) },
          dependsOn: ['p5-notifications'],
        },
        {
          id: 'p5-spend-dashboard',
          title: 'Расходы на сервисы',
          description:
            'Сколько тратим на SMS, хостинг и подключения — в рублях, с автоматическим курсом ЦБ',
          status: 'done',
          category: 'frontend-admin',
          files: [
            'apps/server/src/monitoring/service-spend.lib.ts',
            'docs/adr/0043-service-spend-dashboard.md',
          ],
          endpoints: ['GET /api/admin/monitoring/spend/summary'],
          position: { x: COL(3), y: ROW(P5, 2) },
          dependsOn: ['p5-balance-monitoring'],
        },
      ],
    },
    {
      id: 'phase-6',
      index: 6,
      title: 'Экономика заказа',
      subtitle: 'Цены, скидки, вознаграждения, кураторы',
      weight: 0.1,
      color: '#d97706',
      accent: 'rgba(217, 119, 6, 0.14)',
      heroPosition: { x: 40, y: P6 + 170 },
      tasks: [
        {
          id: 'p6-margin-model',
          title: 'Расчёт цены и вознаграждения',
          description:
            'Скидка пациенту и вознаграждение врачу считаются от реальной наценки (закупка vs розница), а не фиксированным процентом',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/order/pricing.ts', 'docs/adr/0022-margin-based-reward-model.md'],
          position: { x: COL(0), y: ROW(P6, 0) },
        },
        {
          id: 'curator-self-api',
          title: 'Кабинет куратора',
          description: 'Куратор видит своих врачей, начисления и выплаты, указывает реквизиты',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/curator/', 'docs/adr/0023-curator-role-and-rewards.md'],
          endpoints: [
            'GET /api/curator/summary',
            'GET /api/curator/partners',
            'GET /api/curator/accruals',
            'PUT /api/curator/requisites',
          ],
          position: { x: COL(1), y: ROW(P6, 0) },
          dependsOn: ['p6-margin-model'],
        },
        {
          id: 'curator-admin-api',
          title: 'Управление кураторами',
          description: 'Добавление кураторов, привязка врачей, настройка процента и реквизитов',
          status: 'done',
          category: 'frontend-admin',
          pages: ['curators'],
          endpoints: ['CRUD /api/admin/curators', 'PUT /api/admin/curators/:id/requisites'],
          position: { x: COL(2), y: ROW(P6, 0) },
          dependsOn: ['curator-self-api'],
        },
        {
          id: 'curator-payouts-api',
          title: 'Выплаты кураторам',
          description: 'Заявки и движение выплат куратору — по той же схеме, что и врачам',
          status: 'done',
          category: 'frontend-admin',
          pages: ['curator-payouts'],
          endpoints: [
            'GET /api/admin/curator-payouts',
            'POST /api/admin/curator-payouts/create-for/:curatorId',
            'POST /api/curator/payouts',
          ],
          position: { x: COL(3), y: ROW(P6, 0) },
          dependsOn: ['curator-admin-api'],
        },
        {
          id: 'curator-accrual-on-pay',
          title: 'Начисление куратору',
          description: 'Процент куратора считается от заказа и не превышает наценку платформы',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/curator/platform-margin.ts'],
          position: { x: COL(4), y: ROW(P6, 0) },
          dependsOn: ['p6-margin-model'],
        },
        {
          id: 'p6-curator-partner-lk',
          title: 'Кабинет куратора в приложении',
          description: 'Раздел куратора в приложении для врачей: сводка, врачи, реквизиты',
          status: 'done',
          category: 'frontend-partner',
          pages: ['curator'],
          files: ['apps/partner/src/pages/curator/'],
          position: { x: COL(0), y: ROW(P6, 1) },
          dependsOn: ['curator-self-api'],
        },
        {
          id: 'p6-biomaterial',
          title: 'Услуга забора биоматериала',
          description:
            'Забор крови и т.п. добавляется в заказ отдельной позицией без скидки; цены по городам',
          status: 'done',
          category: 'backend',
          files: ['docs/adr/0024-biomaterial-collection-preanalytics.md'],
          position: { x: COL(1), y: ROW(P6, 1) },
          dependsOn: ['p6-margin-model'],
        },
        {
          id: 'p6-satellite-cities',
          title: 'Города-спутники',
          description:
            'Для небольших городов закупочная цена берётся по ближайшему крупному городу',
          status: 'done',
          category: 'backend',
          files: [
            'apps/server/src/order/effective-city.helper.ts',
            'docs/adr/0025-satellite-cities-parent-routing.md',
          ],
          position: { x: COL(2), y: ROW(P6, 1) },
          dependsOn: ['p6-margin-model'],
        },
        {
          id: 'p6-price-lock',
          title: 'Фиксация цены заказа',
          description:
            'Цена держится несколько дней, затем пересчитывается по актуальной; платформа следит за убыточными заказами',
          status: 'done',
          category: 'backend',
          files: ['apps/server/src/order/repricing.ts', 'docs/adr/0038-order-price-lock-window.md'],
          endpoints: ['POST /api/admin/monitoring/margin-watch/check'],
          position: { x: COL(3), y: ROW(P6, 1) },
          dependsOn: ['p6-margin-model'],
        },
        {
          id: 'admin-test-funds',
          title: 'Тестовые начисления',
          description:
            'Сотрудник начисляет врачу тестовые деньги, чтобы проверить вывод без реальных средств; в аналитику они не попадают',
          status: 'done',
          category: 'backend',
          files: ['docs/adr/0044-test-funds-for-payout-flow-testing.md'],
          endpoints: [
            'POST /api/admin/partners/:id/test-funds',
            'DELETE /api/admin/partners/:id/test-funds',
          ],
          position: { x: COL(4), y: ROW(P6, 1) },
        },
        {
          id: 'purchase-price-sync',
          title: 'Закупочные цены',
          description:
            'Закупочные цены лаборатории (основа расчёта наценки) с привязкой к городу и поиском',
          status: 'done',
          category: 'integration',
          pages: ['purchase-prices'],
          endpoints: [
            'POST /api/admin/purchase-prices/sync',
            'GET /api/admin/purchase-prices/missing',
          ],
          position: { x: COL(0), y: ROW(P6, 2) },
          dependsOn: ['p6-margin-model'],
        },
        {
          id: 'purchase-price-excel-import',
          title: 'Загрузка прайса лаборатории',
          description:
            'Загрузка прайса KDL из Excel: закупочные цены, разделы и биоматериал из названий',
          status: 'done',
          category: 'integration',
          endpoints: ['POST /api/admin/purchase-prices/import-excel'],
          files: [
            'apps/server/src/integrations/purchase-prices/',
            'docs/adr/0035-pricelist-catalog-metadata.md',
          ],
          position: { x: COL(1), y: ROW(P6, 2) },
          dependsOn: ['purchase-price-sync'],
        },
      ],
    },
  ],
};
